export interface UserProfile {
  objectId: string | null;
  userPrincipalName: string | null;
  displayName: string | null;
  mail: string | null;
  tenantId: string | null;
  tenantDomain: string | null;
  roles: string[];
  highestRole: string | null;
  accountEnabled: boolean | null;
  department: string | null;
  jobTitle: string | null;
  company: string | null;
  officeLocation: string | null;
  employeeId: string | null;
  mobilePhone: string | null;
  hasPhoto?: boolean | null;
  sources: string[];
}

export interface UserProfileDailySessionCount {
  date: string;
  sessionCount: number;
}

export interface UserProfileActivity {
  totalSessionsAllTime: number;
  averageSessionMinutesAllTime: number | null;
  averageMinutesPerActiveDayAllTime: number | null;
  activeDaysAllTime: number;
  totalMinutesAllTime: number;
  totalSessionsLast90Days: number;
  averageSessionMinutesLast90Days: number | null;
  averageMinutesPerActiveDayLast90Days: number | null;
  activeDaysLast90Days: number;
  totalMinutesLast90Days: number;
  dailySessionsLast7Days: UserProfileDailySessionCount[];
  reportingTimeZone: string;
  lastSignInAt: string | null;
}

/** Map BFF JSON (camelCase or PascalCase) to {@link UserProfileActivity}. */
export function mapProfileActivity(raw: Record<string, unknown>): UserProfileActivity {
  const daily = (raw.dailySessionsLast7Days ?? raw.DailySessionsLast7Days) as
    | Array<Record<string, unknown>>
    | undefined;
  return {
    totalSessionsAllTime: num(raw, 'totalSessionsAllTime', 'TotalSessionsAllTime'),
    averageSessionMinutesAllTime: numOrNull(raw, 'averageSessionMinutesAllTime', 'AverageSessionMinutesAllTime'),
    averageMinutesPerActiveDayAllTime: numOrNull(
      raw,
      'averageMinutesPerActiveDayAllTime',
      'AverageMinutesPerActiveDayAllTime'
    ),
    activeDaysAllTime: num(raw, 'activeDaysAllTime', 'ActiveDaysAllTime'),
    totalMinutesAllTime: num(raw, 'totalMinutesAllTime', 'TotalMinutesAllTime'),
    totalSessionsLast90Days: num(raw, 'totalSessionsLast90Days', 'TotalSessionsLast90Days'),
    averageSessionMinutesLast90Days: numOrNull(
      raw,
      'averageSessionMinutesLast90Days',
      'AverageSessionMinutesLast90Days'
    ),
    averageMinutesPerActiveDayLast90Days: numOrNull(
      raw,
      'averageMinutesPerActiveDayLast90Days',
      'AverageMinutesPerActiveDayLast90Days'
    ),
    activeDaysLast90Days: num(raw, 'activeDaysLast90Days', 'ActiveDaysLast90Days'),
    totalMinutesLast90Days: num(raw, 'totalMinutesLast90Days', 'TotalMinutesLast90Days'),
    dailySessionsLast7Days: (daily ?? []).map((d) => ({
      date: String(d.date ?? d.Date ?? ''),
      sessionCount: Number(d.sessionCount ?? d.SessionCount ?? 0)
    })),
    reportingTimeZone: String(raw.reportingTimeZone ?? raw.ReportingTimeZone ?? ''),
    lastSignInAt: dateOrNull(raw, 'lastSignInAt', 'LastSignInAt')
  };
}

function dateOrNull(raw: Record<string, unknown>, camel: string, pascal: string): string | null {
  const v = raw[camel] ?? raw[pascal];
  if (v == null || v === '') return null;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : v.toISOString();
  }
  return null;
}

function num(raw: Record<string, unknown>, camel: string, pascal: string): number {
  const v = raw[camel] ?? raw[pascal];
  return typeof v === 'number' ? v : Number(v ?? 0);
}

function numOrNull(raw: Record<string, unknown>, camel: string, pascal: string): number | null {
  const v = raw[camel] ?? raw[pascal];
  if (v == null) return null;
  return typeof v === 'number' ? v : Number(v);
}
